import { useState, useEffect, useCallback } from "react";
import { GestionHeader } from "./SiteHeader";
import { apiGet, apiSend, apiFetch, errorMessage } from "./api";
import "./Coupons.css";

function formatDate(ts) {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function discountLabel(c) {
  if (c.percent_off != null) return `-${c.percent_off} %`;
  if (c.amount_off != null) {
    return `-${c.amount_off.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`;
  }
  return "—"; // coupon supprimé côté Stripe : le code n'accorde plus rien
}

function durationLabel(c) {
  if (c.whole_subscription) return "Tout l'abonnement";
  if (c.duration_months == null) return "—";
  return c.duration_months === 1 ? "1 mois" : `${c.duration_months} mois`;
}

function CreateCouponModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    code: "",
    discount_type: "percent",
    value: "",
    duration: "forever", // "forever" = tout l'abonnement, "months" = N mois
    duration_months: "",
    expires_at: "",
    max_redemptions: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.duration === "months" && !form.duration_months) {
      setError("Indiquez le nombre de mois couverts par la réduction");
      return;
    }
    setLoading(true);
    try {
      const body = {
        name: form.name,
        code: form.code.trim() || null,
        discount_type: form.discount_type,
        value: parseFloat(form.value),
        duration_months:
          form.duration === "months" ? parseInt(form.duration_months) : null,
        expires_at: form.expires_at || null,
        max_redemptions: form.max_redemptions
          ? parseInt(form.max_redemptions)
          : null,
      };
      const created = await apiSend("/api/billing/admin/coupons", "POST", body,
        "Erreur lors de la création du code");
      onCreated(created);
      return;
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de la création du code"));
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Créer un code de réduction</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="modal-error">{error}</div>}

          <label>Nom</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Offre de lancement"
            required
          />
          <p className="modal-hint">
            Visible par le client sur la page de paiement et ses factures.
          </p>

          <label>Code (facultatif)</label>
          <input
            name="code"
            value={form.code}
            onChange={handleChange}
            placeholder="Laisser vide pour un code généré automatiquement"
            maxLength={40}
            style={{ textTransform: "uppercase" }}
          />

          <label>Type de réduction</label>
          <select name="discount_type" value={form.discount_type} onChange={handleChange}>
            <option value="percent">Pourcentage (%)</option>
            <option value="amount">Montant direct (€)</option>
          </select>

          <label>
            {form.discount_type === "percent"
              ? "Pourcentage de réduction"
              : "Montant de la réduction (€)"}
          </label>
          <input
            name="value"
            type="number"
            value={form.value}
            onChange={handleChange}
            min={form.discount_type === "percent" ? 1 : 0.01}
            max={form.discount_type === "percent" ? 100 : undefined}
            step={form.discount_type === "percent" ? 1 : 0.01}
            required
          />

          <label>S'applique sur</label>
          <select name="duration" value={form.duration} onChange={handleChange}>
            <option value="forever">Tout l'abonnement</option>
            <option value="months">Un nombre de mois</option>
          </select>
          {form.duration === "months" && (
            <>
              <label>Nombre de mois</label>
              <input
                name="duration_months"
                type="number"
                value={form.duration_months}
                onChange={handleChange}
                min={1}
                required
              />
              <p className="modal-hint">
                La réduction s'applique aux factures émises pendant les N
                premiers mois de l'abonnement, puis le tarif normal reprend.
              </p>
            </>
          )}

          <label>Valable jusqu'au (facultatif)</label>
          <input
            name="expires_at"
            type="date"
            value={form.expires_at}
            onChange={handleChange}
          />
          <p className="modal-hint">
            Passé cette date, le code ne peut plus être saisi. Vide = sans
            limite de temps.
          </p>

          <label>Nombre d'utilisations maximum (facultatif)</label>
          <input
            name="max_redemptions"
            type="number"
            value={form.max_redemptions}
            onChange={handleChange}
            min={1}
            placeholder="Illimité"
          />

          <button type="submit" className="modal-submit" disabled={loading}>
            {loading ? "Création..." : "Créer le code"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Coupons({ user, onNavigate, onLogout }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createdCode, setCreatedCode] = useState("");

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet("/api/billing/admin/coupons",
        "Impossible de charger les codes de réduction");
      setCoupons(data.coupons);
      setError("");
    } catch (e) {
      setCoupons([]);
      setError(errorMessage(e, "Impossible de charger les codes de réduction"));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleDeactivate = async (promo) => {
    if (!window.confirm(`Désactiver le code ${promo.code} ? Il ne pourra plus être utilisé.`)) {
      return;
    }
    try {
      await apiFetch(`/api/billing/admin/coupons/${promo.id}`, { method: "DELETE" },
        "Impossible de désactiver le code");
      fetchCoupons();
    } catch (e) {
      setError(errorMessage(e, "Impossible de désactiver le code"));
    }
  };

  return (
    <div className="cp-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Codes de réduction"
        onBack={() => onNavigate("admin")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      >
        <button
          className="rc-btn rc-btn-primary site-header-btn"
          onClick={() => setShowCreate(true)}
        >
          + Créer un code
        </button>
      </GestionHeader>

      <main className="cp-main">
        {error && <div className="cp-error">{error}</div>}
        {createdCode && (
          <div className="cp-success">
            Code <strong className="cp-mono">{createdCode}</strong> créé — les
            clients peuvent le saisir sur la page de paiement.
            <button className="cp-success-close" onClick={() => setCreatedCode("")}>
              &times;
            </button>
          </div>
        )}

        {loading ? (
          <p className="cp-empty">Chargement...</p>
        ) : coupons.length === 0 ? (
          <p className="cp-empty">
            Aucun code de réduction pour le moment. Créez-en un avec le bouton
            ci-dessus.
          </p>
        ) : (
          <div className="cp-table-scroll">
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Nom</th>
                  <th>Réduction</th>
                  <th>S'applique sur</th>
                  <th>Valable jusqu'au</th>
                  <th className="cp-num">Utilisations</th>
                  <th>Statut</th>
                  <th>
                    <span className="cp-sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id} className={c.active ? "" : "cp-row-inactive"}>
                    <td className="cp-mono">{c.code}</td>
                    <td>{c.name || "—"}</td>
                    <td className="cp-discount">{discountLabel(c)}</td>
                    <td>{durationLabel(c)}</td>
                    <td>{formatDate(c.expires_at)}</td>
                    <td className="cp-num">
                      {c.times_redeemed}
                      {c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
                    </td>
                    <td>
                      <span className={`cp-status ${c.active ? "cp-status-active" : "cp-status-inactive"}`}>
                        {c.active ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="cp-actions">
                      {c.active && (
                        <button
                          className="cp-deactivate"
                          onClick={() => handleDeactivate(c)}
                        >
                          Désactiver
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showCreate && (
        <CreateCouponModal
          onClose={() => setShowCreate(false)}
          onCreated={(created) => {
            setShowCreate(false);
            // Le code est souvent généré par Stripe : on l'affiche pour que
            // l'admin puisse le copier sans le chercher dans le tableau.
            setCreatedCode(created && created.code ? created.code : "");
            fetchCoupons();
          }}
        />
      )}
    </div>
  );
}

export default Coupons;
